import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MagicLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setToken } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyMagicLink(token);
    } else {
      setStatus('error');
      setError('Kein Token gefunden');
    }
  }, [searchParams]);

  const verifyMagicLink = async (token) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/magic-login?token=${token}`);
      
      // Set auth data
      localStorage.setItem('token', res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
      
      setStatus('success');
      
      // Redirect after 2 seconds
      setTimeout(() => {
        if (res.data.user.role === 'client') {
          navigate('/dashboard');
        } else {
          navigate('/admin');
        }
      }, 2000);
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Link ungültig oder abgelaufen');
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F8FB] flex items-center justify-center p-4" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center"
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-teal-500 mx-auto mb-4 animate-spin" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">
              Anmeldung läuft...
            </h1>
            <p className="text-slate-500">
              Bitte warte einen Moment.
            </p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">
              Erfolgreich angemeldet! 🎉
            </h1>
            <p className="text-slate-500 mb-4">
              Du wirst gleich weitergeleitet...
            </p>
            <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ duration: 2 }}
                className="h-full bg-teal-500 rounded-full"
              />
            </div>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">
              Anmeldung fehlgeschlagen
            </h1>
            <p className="text-slate-500 mb-6">
              {error}
            </p>
            <Button asChild className="w-full bg-slate-900 hover:bg-slate-800">
              <Link to="/login">Zurück zum Login</Link>
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
